import { useEffect, useRef } from 'react';
import { useTree } from '@headless-tree/react';
import { syncDataLoaderFeature, selectionFeature } from '@headless-tree/core';
import { useQuery } from '@tanstack/react-query';

import { useXFetch } from '~/shared/utils/useXFetch';
import { useAuthContext } from '~/shared/context/useAuthContext';
import { getOrgAccountsScopeValue } from '~/shared/utils/organizationUtils';
import {
  configurationTreeQueryKey,
  getConfigurationTree,
} from '~/settings/components/ConfigurationsPage/helpers/configurationApi';
import { transformTreeResponse } from '~/settings/components/ConfigurationsPage/helpers/transformConfigResponse';
import {
  getTreeItem,
  getTreeChildrenNodes,
} from '~/settings/components/ConfigurationsPage/helpers/treeDataLoader';

import { useExpandAllOnSearch } from './useExpandAllOnSearch';

import type { ConfigTreeNode } from '~/settings/components/ConfigurationsPage/types';

export function useConfigurationTree(mode: 'full' | 'edited' = 'full', searchQuery = '') {
  const api = useXFetch();
  const { accounts, activeTenantId } = useAuthContext();
  const accountsScope = getOrgAccountsScopeValue(accounts, activeTenantId);
  const treeDataRef = useRef<ConfigTreeNode | null>(null);

  const {
    data: treeData,
    isLoading,
    isFetching,
    error,
  } = useQuery({
    queryKey: configurationTreeQueryKey(accounts, mode, accountsScope),
    queryFn: async () => {
      const response = await getConfigurationTree(api, mode, { accountsScope });
      return transformTreeResponse(response, accounts);
    },
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  useEffect(() => {
    if (treeData) {
      treeDataRef.current = treeData;
    }
  }, [treeData]);

  const tree = useTree<ConfigTreeNode>({
    rootItemId: 'root',
    getItemName: (item) => item.getItemData().name,
    isItemFolder: (item) => {
      const currentData = treeDataRef.current;
      if (!currentData) {
        return false;
      }
      const children = getTreeChildrenNodes(item.getId(), currentData);
      return children.length > 0;
    },
    dataLoader: {
      getItem: (itemId: string) => {
        // Use ref to always get latest treeData
        // syncDataLoaderFeature requires synchronous functions, not promises
        const currentData = treeDataRef.current;
        if (!currentData) {
          return undefined as unknown as ConfigTreeNode;
        }
        return getTreeItem(itemId, currentData) as ConfigTreeNode | undefined;
      },
      getChildren: (itemId: string) => {
        // Use ref to always get latest treeData
        // syncDataLoaderFeature requires synchronous functions, not promises
        const currentData = treeDataRef.current;
        if (!currentData) {
          return [];
        }
        return getTreeChildrenNodes(itemId, currentData);
      },
    } as {
      getItem: (itemId: string) => ConfigTreeNode | Promise<ConfigTreeNode>;
      getChildren: (itemId: string) => string[] | Promise<string[]>;
    },
    features: [syncDataLoaderFeature, selectionFeature],
    initialState: {
      expandedItems: treeData ? [treeData.id] : [],
    },
  });

  // Rebuild tree and expand root node when data loads
  useEffect(() => {
    if (treeData && !isLoading) {
      // Rebuild the tree to ensure it picks up the new data
      try {
        tree.rebuildTree();
      } catch (error) {
        console.debug('Tree rebuild:', error);
      }

      // Small delay to ensure tree has processed the data
      const timer = setTimeout(() => {
        try {
          const items = tree.getItems();
          const rootItem = items.find((item) => item.getId() === treeData.id);
          if (rootItem && rootItem.isFolder() && !rootItem.isExpanded()) {
            rootItem.expand();
          }
        } catch (error) {
          console.debug('Tree expansion:', error);
        }
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [treeData, isLoading, tree]);

  // Log errors if any
  useEffect(() => {
    if (error) {
      console.error('Failed to load tree data:', error);
    }
  }, [error]);

  // Runs after the rebuild effect above, so it expands a freshly built tree.
  useExpandAllOnSearch(tree, !!searchQuery.trim(), treeData ?? null);

  return { tree, treeData, isLoading, hasData: !!treeData };
}
